import { Children, isValidElement, cloneElement, FC, ElementType } from 'react';
import {
  omit,
  compose,
  not,
  when,
  is,
  filter,
  curry,
  or,
  isNil,
  isEmpty,
  map,
  flatten,
} from 'ramda';

import { kebabize, clean } from 'yafrpl/strings';

export const addClass = (
  classToAdd: string | string[] | undefined,
  { className = '' }: { className?: string }
): string => {
  let newClass: string[];

  if (classToAdd) {
    if (classToAdd instanceof Array) {
      newClass = classToAdd;
    } else {
      newClass = [classToAdd];
    }
  } else {
    newClass = [];
  }

  return newClass
    .concat(className ? [processClassName(className)] : [])
    .join(' ');
};

export const getDisplayName = (
  Component: ElementType | string | undefined
): string | undefined => {
  if (is(String, Component)) {
    return Component as string;
  }

  if (!Component) {
    return undefined;
  }

  return (Component as FC).displayName || (Component as FC).name || 'Component';
};

export const filterProps = (
  Component: ElementType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: Record<string, any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> => {
  const omitted = [
    'children',
    'className',
    'as',
    'type',
    't',
    'i18n',
    'shadowKey',
    'language',
    'dispatch',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...((Component as any).handledProps || []),
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ret: Record<string, any> = omit(omitted, props);

  const className = getClassName(Component, props);
  className && (ret.className = className);

  return ret;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isVoid = (d: any): boolean => or(isNil(d), isEmpty(d));
const isNotVoid = compose(not, isVoid);

export const sortProps = (
  Component: FC,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: Record<string, any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: Record<string, any> = {};
  map((p) => {
    if (p && isNotVoid(props[p])) {
      result[p] = props[p];
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, (Component as any).handledProps);
  return result;
};

const whenNoCSSClass = (Component: ElementType): string | undefined => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((Component as any).noCSSClass) {
    return undefined;
  }
  return getDisplayName(Component);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalize = (str: any): string => clean(str).replace('Component', '');

export const createCssClassName: (Component: ElementType) => string = compose(
  (str: string | void): string => str || '',
  kebabize,
  normalize,
  whenNoCSSClass
);

const processClassName = when(is(Array), (array) =>
  flatten(array).filter(Boolean).join(' ')
);

export const getClassName = (
  Component: ElementType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: Record<string, any>
): string => {
  const defaultClassName = createCssClassName(Component);
  return processClassName([props.className, defaultClassName]);
};

export const filterValidElement = compose(
  filter(isValidElement),
  Children.toArray
);

export const clone = curry((props, element) =>
  cloneElement(element, props, element.props.children)
);
